/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import type { TablePaginationConfig, TableProps } from "antd"
import { Table, Tag, Typography } from "antd"
import { UserCellLink } from "@/components/UserCellLink"

const { Text } = Typography
import type React from "react"
import { useTableScroll } from "@/hooks/useTableScroll"
import "@/styles/table.full.css"
import { formatDateTime } from "@/utils/dateUtils"

interface VocabularyTableProps {
  data: any[]
  loading: boolean
  selectedRowKeys: React.Key[]
  onSelectionChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => void
  pagination?: TableProps["pagination"]
}

const VocabularyTable: React.FC<VocabularyTableProps> = ({
  data,
  loading,
  selectedRowKeys,
  onSelectionChange,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 85,
      align: "center",
      render: (value: number) => <span className="font-semibold">{value}</span>,
    },
    {
      title: "Từ vựng / Cụm từ",
      key: "vocabulary_info",
      width: 220,
      fixed: "left",
      render: (_: any, record: any) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-bold text-primary text-sm">{record.text}</span>
          <div className="mt-0.5 flex flex-wrap gap-1">
            {record.part_of_speech && (
              <Tag color="orange" className="m-0 px-1 py-0 text-[10px]">
                {record.part_of_speech}
              </Tag>
            )}
            {record.ipa && (
              <span>
                <Text type="secondary" className="font-mono text-xs">
                  {record.ipa}
                </Text>
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Ý nghĩa & Định nghĩa",
      key: "meaning_definition",
      width: 250,
      render: (_: any, record: any) => {
        if (!record.meaning && !record.definition) return <Text type="secondary">—</Text>
        return (
          <div className="text-xs">
            {record.meaning && (
              <div>
                <Text strong>{record.meaning}</Text>
              </div>
            )}
            {record.definition && (
              <div className="mt-1">
                <Text type="secondary" italic>
                  {record.definition}
                </Text>
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: "Ví dụ & Dịch nghĩa",
      key: "example_info",
      width: 250,
      render: (_: any, record: any) => {
        if (!record.example) return <Text type="secondary">—</Text>
        return (
          <div className="text-xs">
            <div>
              <Text italic>"{record.example}"</Text>
            </div>
            {record.example_meaning && (
              <div className="mt-0.5">
                <Text type="secondary">{record.example_meaning}</Text>
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: "Tham chiếu",
      dataIndex: "reference_type",
      key: "reference_type",
      width: 110,
      align: "center",
      render: (value: string) => {
        const colors: Record<string, string> = {
          word: "blue",
          phrasal_verb: "cyan",
          collocation: "purple",
          idiom: "magenta",
          phrase: "geekblue",
        }
        return <Tag color={colors[value] || "default"}>{value}</Tag>
      },
    },
    {
      title: "Người lưu",
      key: "user",
      width: 180,
      render: (_: any, record: any) => <UserCellLink user={record.user} userId={record.user_id} />,
    },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      key: "created_at",
      width: 130,
      align: "center",
      render: (value: string) => (value ? formatDateTime(value, "dd/mm/yy hh:mm") : "_"),
    },
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectionChange,
  }

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

VocabularyTable.displayName = "VocabularyTable"

export default VocabularyTable
